import { useTranslation } from 'react-i18next';
import type { SessionResults } from '../types';

interface ExamResultsTableProps {
  results: SessionResults;
  showOnlyIncorrect?: boolean;
}

export default function ExamResultsTable({ results, showOnlyIncorrect = false }: ExamResultsTableProps) {
  const { t } = useTranslation();
  const answers = showOnlyIncorrect
    ? results.answers.filter((a) => !a.is_correct)
    : results.answers;

  if (answers.length === 0) {
    return (
      <p className="py-8 text-center text-slate-500 dark:text-slate-400">
        {showOnlyIncorrect ? `${t('study.examResultsTable.allCorrect')} 🎉` : t('study.examResultsTable.noAnswers')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-white/10">
        <thead>
          <tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50/60 dark:bg-white/5">
            <th scope="col" className="px-4 py-3">
              {t('study.examResultsTable.headerNumber')}
            </th>
            <th scope="col" className="px-4 py-3">
              {t('study.examResultsTable.headerQuestion')}
            </th>
            <th scope="col" className="px-4 py-3">
              {t('study.examResultsTable.headerYourAnswer')}
            </th>
            <th scope="col" className="px-4 py-3">
              {t('study.examResultsTable.headerCorrectAnswer')}
            </th>
            <th scope="col" className="px-4 py-3 text-center">
              {t('study.examResultsTable.headerResult')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-white/5">
          {answers.map((answer, idx) => (
            <tr
              key={answer.question_id}
              className={`transition-colors ${
                answer.is_correct
                  ? 'ring-2 ring-inset ring-success/20 bg-success/5'
                  : 'ring-2 ring-inset ring-error/20 bg-error/5'
              }`}
            >
              <td className="whitespace-nowrap px-4 py-3 font-mono text-sm tabular-nums text-slate-500 dark:text-slate-400">
                {idx + 1}
              </td>
              <td className="max-w-xs truncate px-4 py-3 text-sm text-slate-800 dark:text-slate-200" title={answer.question_text}>
                {answer.question_text.length > 80
                  ? `${answer.question_text.slice(0, 80)}…`
                  : answer.question_text}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium">
                <span
                  className={
                    answer.is_correct
                      ? 'text-success'
                      : 'text-error'
                  }
                >
                  {answer.given_answer}
                </span>
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-success">
                {answer.correct_answer}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-center text-lg">
                {answer.is_correct ? (
                  <span className="text-success" aria-label={t('study.examResultsTable.correct')}>✓</span>
                ) : (
                  <span className="text-error" aria-label={t('study.examResultsTable.incorrect')}>✗</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
